import { useEffect, useRef, useState } from 'react';

import {
  uploadToFirebaseStorage,
  type FirebaseUploadResult,
} from '@/lib/firebase/storageUpload';
import { resizeImageFile } from '@/lib/image/resizeImage';

import { validateAIUploadFile } from './FilePicker';

type UploadState =
  | { status: 'idle'; error: string | null }
  | {
      blob: Blob;
      error: string | null;
      fileName: string;
      isImage: false;
      previewUrl?: undefined;
      status: 'selected';
    }
  | {
      blob: Blob;
      error: string | null;
      fileName: string;
      previewUrl: string;
      status: 'cropping';
    }
  | {
      error: string | null;
      fileName: string;
      previewUrl?: string;
      progress: number;
      status: 'uploading';
    }
  | {
      error: string | null;
      fileName: string;
      isImage: boolean;
      previewUrl?: string;
      result: FirebaseUploadResult;
      status: 'ready' | 'submitted';
    }
  | {
      error: string;
      fileName?: string;
      previewUrl?: string;
      status: 'error';
    };

interface LastUpload {
  blob: Blob;
  fileName: string;
  isImage: boolean;
  previewUrl?: string;
}

function createPreviewUrl(blob: Blob) {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return '';
  }

  return URL.createObjectURL(blob);
}

function revokePreviewUrl(value: string | undefined) {
  if (value && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(value);
  }
}

export function useFileUpload() {
  const [state, setState] = useState<UploadState>({
    error: null,
    status: 'idle',
  });
  const previewUrlRef = useRef<string | undefined>(undefined);
  const lastUploadRef = useRef<LastUpload | null>(null);

  useEffect(() => () => {
    revokePreviewUrl(previewUrlRef.current);
  }, []);

  function setPreviewUrl(previewUrl: string | undefined) {
    if (previewUrlRef.current && previewUrlRef.current !== previewUrl) {
      revokePreviewUrl(previewUrlRef.current);
    }

    previewUrlRef.current = previewUrl;
  }

  async function uploadBlob(
    blob: Blob,
    fileName: string,
    isImage: boolean,
    previewUrl?: string,
  ) {
    lastUploadRef.current = {
      blob,
      fileName,
      isImage,
      previewUrl,
    };
    setState({
      error: null,
      fileName,
      previewUrl,
      progress: 0,
      status: 'uploading',
    });

    try {
      const result = await uploadToFirebaseStorage(blob, fileName, {
        onProgress: (progress) => {
          setState((currentState) =>
            currentState.status === 'uploading'
              ? {
                  ...currentState,
                  progress,
                }
              : currentState,
          );
        },
      });

      setState({
        error: null,
        fileName,
        isImage,
        previewUrl,
        result,
        status: 'ready',
      });
    } catch (error) {
      setState({
        error:
          error instanceof Error
            ? error.message
            : 'Unable to upload the selected file.',
        fileName,
        previewUrl,
        status: 'error',
      });
    }
  }

  async function selectFile(file: File) {
    const validationError = validateAIUploadFile(file);

    if (validationError) {
      setState({
        error: validationError,
        status: 'idle',
      });
      return;
    }

    const isImage = file.type.startsWith('image/');

    if (!isImage) {
      setState({
        blob: file,
        error: null,
        fileName: file.name,
        isImage: false,
        previewUrl: undefined,
        status: 'selected',
      });
      return;
    }

    try {
      const resizedBlob = await resizeImageFile(file);
      const previewUrl = createPreviewUrl(resizedBlob);
      setPreviewUrl(previewUrl);
      setState({
        blob: resizedBlob,
        error: null,
        fileName: file.name,
        previewUrl,
        status: 'cropping',
      });
    } catch (error) {
      setState({
        error:
          error instanceof Error
            ? error.message
            : 'Unable to prepare the selected image.',
        status: 'idle',
      });
    }
  }

  async function confirmCrop(blob: Blob) {
    if (state.status !== 'cropping') {
      return;
    }

    await uploadBlob(blob, state.fileName, true, state.previewUrl);
  }

  async function confirmSelectedFile() {
    if (state.status !== 'selected') {
      return;
    }

    await uploadBlob(state.blob, state.fileName, false);
  }

  async function useFullImage() {
    if (state.status !== 'cropping') {
      return;
    }

    await uploadBlob(state.blob, state.fileName, true, state.previewUrl);
  }

  function reset() {
    setPreviewUrl(undefined);
    lastUploadRef.current = null;
    setState({
      error: null,
      status: 'idle',
    });
  }

  async function retry() {
    if (!lastUploadRef.current) {
      return;
    }

    const lastUpload = lastUploadRef.current;
    await uploadBlob(
      lastUpload.blob,
      lastUpload.fileName,
      lastUpload.isImage,
      lastUpload.previewUrl,
    );
  }

  function markSubmitted() {
    setState((currentState) =>
      currentState.status === 'ready'
        ? {
            ...currentState,
            status: 'submitted',
          }
        : currentState,
    );
  }

  // The service worker kept the file, which used it up, and the server has since
  // refused it. It is still uploaded.
  function markReady() {
    setState((currentState) =>
      currentState.status === 'submitted'
        ? {
            ...currentState,
            status: 'ready',
          }
        : currentState,
    );
  }

  return {
    confirmSelectedFile,
    confirmCrop,
    markReady,
    markSubmitted,
    reset,
    retry,
    selectFile,
    state,
    useFullImage,
  };
}
